import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Chat, User } from "snudbud-backbone-sdk";
import { useAPI } from "../../contexts/APIContext";
import { useNotification } from "../../contexts/NotificationContext";
import handleError from "../../utils/handleError";
import usePagination from "../../utils/usePagination";
import ChatHeader from "./components/ChatHeader";
import ExitConfirmationModal from "./components/ExitConfirmationModal";
import MessageInput from "./components/MessageInput";
import MessageList from "./components/MessageList";
import ProgressBar from "./components/ProgressBar";
import initializeStyles from "./styles";

export default function ChatPage() {
  const { userId } = useParams<{ userId: string }>();
  const { api, user } = useAPI();
  const { fetchUnreadCount } = useNotification();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<User.FetchProfileReturn>();
  const itemsPerPage = 50;
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const previousMessageCountRef = useRef(0);
  const [conversationTurns, setConversationTurns] = useState<number>(0);
  const [showExitConfirmation, setShowExitConfirmation] = useState<boolean>(false);
  const [exitDestination, setExitDestination] = useState<string>("");
  const [showProgressBar, setShowProgressBar] = useState<boolean>(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);
  const sessionTargetMessages = 10;

  // Use pagination hook to load messages
  const messagesPagination = usePagination<Chat.Message>({
    fn: async ({ offset, limit }) => {
      if (!userId) return { count: 0, items: [] };

      // For initial load, get the most recent messages
      const result = await api.chat.queryMessages({
        userId,
        offset,
        limit: limit || itemsPerPage,
      });

      return { count: result.count, items: result.messages };
    },
    initial: true,
    itemsPerPage,
  });

  // Calculate conversation turns from message list
  const calculateConversationTurns = useCallback((messages: Chat.Message[]) => {
    if (!messages.length) return 0;

    // Sort messages by creation time (oldest first)
    const sortedMessages = [...messages].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

    let turns = 1; // First message counts as one turn
    let previousSender = sortedMessages[0].fromUserId;

    // Count turns (each time sender changes)
    for (let i = 1; i < sortedMessages.length; i++) {
      const currentSender = sortedMessages[i].fromUserId;
      if (currentSender !== previousSender) {
        turns++;
        previousSender = currentSender;
      }
    }

    return turns;
  }, []);

  // Update conversation turns when messages change
  useEffect(() => {
    if (messagesPagination.data.length > 0) {
      const turns = calculateConversationTurns(messagesPagination.data);
      setConversationTurns(turns);
    }
  }, [messagesPagination.data, calculateConversationTurns]);

  // Load partner profile
  useEffect(() => {
    if (!userId) return;

    const loadPartnerProfile = async () => {
      try {
        const result = await api.user.fetchProfile({ userId });
        setProfile(result);
      } catch (error) {
        handleError(error);
        navigate("/chats");
      }
    };

    loadPartnerProfile();
  }, [userId, api, navigate]);

  // Mark messages as read when loaded
  useEffect(() => {
    if (!userId || messagesPagination.data.length === 0) return;

    const markMessagesAsRead = async () => {
      try {
        await api.chat.readMessages({ userId });
        // Update unread count after marking messages as read
        await fetchUnreadCount();
      } catch (error) {
        handleError(error);
      }
    };

    markMessagesAsRead();
  }, [userId, api, messagesPagination.data, fetchUnreadCount]);

  // Scroll to bottom on initial load and when new messages arrive
  useEffect(() => {
    // Wait for messages to be loaded
    if (messagesPagination.isLoading || messagesPagination.data.length === 0) return;

    const currentCount = messagesPagination.data.length;
    const isNewMessageAdded = currentCount > previousMessageCountRef.current;

    // Scroll immediately to bottom on initial load
    if (!initialLoadComplete) {
      // Use immediate scrolling for initial load
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView();
        setInitialLoadComplete(true);
      }
    }
    // Scroll to bottom when new messages arrive (but not when loading older messages)
    else if (isNewMessageAdded && !isLoadingOlderMessages) {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }

    // Update previous count
    previousMessageCountRef.current = currentCount;
  }, [messagesPagination.isLoading, messagesPagination.data.length, initialLoadComplete, isLoadingOlderMessages]);

  // Force scroll to bottom on component mount
  useEffect(() => {
    // Create a small delay to ensure DOM is ready
    const scrollTimer = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView();
      }
    }, 100);

    return () => clearTimeout(scrollTimer);
  }, []);

  // Listen for incoming messages
  useEffect(() => {
    if (!api.socket || !userId) return;

    const handleIncomingMessage = async (message: Chat.Message) => {
      // Only handle messages from this conversation
      if (message.fromUserId !== userId && message.toUserId !== userId) return;

      // Add the message to our list
      messagesPagination.setData((prev) => {
        const newMessages = [...prev, message];
        return newMessages;
      });

      // Mark as read
      try {
        await api.chat.readMessages({ userId });
        // Update unread count after marking messages as read
        await fetchUnreadCount();
      } catch (error) {
        handleError(error);
      }
    };

    api.socket.on("chat:message", handleIncomingMessage);

    return () => {
      api.socket?.off("chat:message", handleIncomingMessage);
    };
  }, [api.socket, userId, messagesPagination, fetchUnreadCount]);

  // Handle loading more messages when scrolling up
  const handleScroll = useCallback(() => {
    // Skip scroll handling during initial load or if already loading
    if (!initialLoadComplete || !listRef.current || isLoadingMoreRef.current) return;

    const { scrollTop } = listRef.current;

    // If scrolled near top and not currently loading, load more
    if (scrollTop < 50 && !messagesPagination.isLoading && messagesPagination.data.length < messagesPagination.count) {
      isLoadingMoreRef.current = true;
      setIsLoadingOlderMessages(true);
      const scrollHeight = listRef.current.scrollHeight;

      // Load more messages
      messagesPagination
        .load({ append: true })
        .then(() => {
          // Maintain scroll position after new messages are loaded
          if (listRef.current) {
            const newScrollHeight = listRef.current.scrollHeight;
            listRef.current.scrollTop = newScrollHeight - scrollHeight;
          }

          // Reset loading flags after a short delay to prevent rapid consecutive loads
          setTimeout(() => {
            isLoadingMoreRef.current = false;
            setIsLoadingOlderMessages(false);
          }, 300);
        })
        .catch(() => {
          isLoadingMoreRef.current = false;
          setIsLoadingOlderMessages(false);
        });
    }
  }, [messagesPagination, initialLoadComplete]);

  // Attach scroll event listener
  useEffect(() => {
    const currentRef = listRef.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
      return () => currentRef.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  // Handle exit attempts
  const handleExitAttempt = (destination: string = "/chats") => {
    setExitDestination(destination);
    setShowExitConfirmation(true);
  };

  // Confirm exit
  const confirmExit = () => {
    setShowExitConfirmation(false);
    navigate(exitDestination);
  };

  // Cancel exit
  const cancelExit = () => {
    setShowExitConfirmation(false);
    setExitDestination("");
  };

  // Handle goal reached celebration
  useEffect(() => {
    if (conversationTurns >= sessionTargetMessages) {
      // Show celebration for 5 seconds before hiding progress bar
      setTimeout(() => {
        setShowProgressBar(false);
      }, 5000);
    }
  }, [conversationTurns, sessionTargetMessages]);

  // Add animation styles to document
  useEffect(() => {
    const cleanup = initializeStyles();
    return cleanup;
  }, []);

  return (
    <div className="h-screen w-full flex flex-col bg-white">
      <ChatHeader profile={profile} onExitAttempt={handleExitAttempt} />

      {showProgressBar && (
        <ProgressBar
          conversationTurns={conversationTurns}
          sessionTargetMessages={sessionTargetMessages}
          profile={profile}
        />
      )}

      <MessageList
        messagesPagination={messagesPagination}
        isLoadingOlderMessages={isLoadingOlderMessages}
        userId={userId}
        user={user}
        profile={profile}
        messagesEndRef={messagesEndRef}
        listRef={listRef}
      />

      <MessageInput userId={userId} api={api} />

      {showExitConfirmation && (
        <ExitConfirmationModal
          onConfirm={confirmExit}
          onCancel={cancelExit}
          profile={profile}
          conversationTurns={conversationTurns}
          sessionTargetMessages={sessionTargetMessages}
        />
      )}
    </div>
  );
}
